import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Briefcase, PartyPopper, Music, ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import SectionHeading from "@/components/shared/SectionHeading";
import TestimonialCard from "@/components/shared/TestimonialCard";
import VideoCarousel from "@/components/shared/VideoCarousel";
import ContactButtons from "@/components/shared/ContactButtons";

const Events = () => {
  const videos = [
    { src: "https://www.youtube.com/embed/gYEoAi-Pe8M", title: "Event Performance Showreel" },
    { src: "https://www.youtube.com/embed/LAdKfpsZaUc", title: "Olivia Flemming Showreel" },
    { src: "https://www.youtube.com/embed/iOg-K-LO67U", title: "Wedding Performance" }
  ];

  const eventTypes = [
    {
      icon: <PartyPopper className="w-8 h-8" />,
      title: "Private Parties",
      description: "Birthdays, anniversaries, garden parties, and special celebrations. Add a touch of elegance with live classical and contemporary vocals."
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Corporate Events",
      description: "Gala dinners, awards ceremonies, product launches, and corporate hospitality. Professional entertainment that impresses."
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: "Special Occasions",
      description: "Charity events, religious ceremonies, seasonal celebrations, and more. Tailored performances for any occasion."
    }
  ];

  const styles = [
    "Classical arias and operatic pieces",
    "Contemporary crossover favourites",
    "Musical theatre and show tunes",
    "Jazz and swing standards",
    "Popular hits and ballads",
    "Traditional and sacred music"
  ];

  const testimonials = [
    {
      quote: "Olivia's performance at our charity gala was simply outstanding. She captivated the entire room.",
      author: "Amanda K.",
      role: "Charity Event Organiser"
    },
    {
      quote: "We hired Olivia for my mother's 80th birthday. She was wonderful—professional, warm, and her voice was incredible.",
      author: "James T.",
      role: "Private Party"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div className="absolute inset-0">
          <img
            src="/Olivia-singing-at-private-event-1.webp"
            alt="Olivia performing at an event"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Star className="w-10 h-10 text-gold mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-semibold text-white mb-4">
              Event Entertainment
            </h1>
            <p className="text-xl md:text-2xl text-cream/90 font-light mb-8">
              Elegant live music for private and corporate occasions
            </p>
            
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-md font-medium text-lg hover:bg-primary/90 transition-all"
            >
              Enquire About Your Event <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading 
            title="Events I Perform At"
            subtitle="From intimate gatherings to grand corporate affairs"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {eventTypes.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-cream/50 rounded-lg"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full text-gold mb-4">
                  {event.icon}
                </div>
                <h3 className="text-xl font-playfair font-semibold text-secondary mb-3">
                  {event.title}
                </h3>
                <p className="text-secondary/70">
                  {event.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Musical Styles */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="Versatile Repertoire"
            subtitle="A wide range of styles to suit any event atmosphere"
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {styles.map((style, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
              >
                <Music className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-secondary">{style}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Showreel */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <SectionHeading 
            title="Performance Highlights"
            subtitle="A glimpse of what I can bring to your event"
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <VideoCarousel videos={videos} />
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-4">
          <SectionHeading 
            title="Client Feedback"
            subtitle="What event organisers say about my performances"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-cream mb-4">
              Make Your Event Unforgettable
            </h2>
            <p className="text-lg text-cream/80 mb-8">
              Let's discuss how live music can elevate your event. Get in touch for a friendly chat about your requirements.
            </p>
            
            <ContactButtons variant="light" className="mb-6" />
            
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-cream hover:text-primary transition-colors"
            >
              Or fill out my enquiry form <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-cream/50 text-sm mt-6">
              Read more: <Link to="/journal/live-music-hotels-events-west-midlands" className="underline underline-offset-4 hover:text-primary">live music for hotels and private events in the West Midlands</Link>.
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Events;
